import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import { Group, Image, Button } from "@mantine/core";
import { ReactComponent as BoughtIcon } from "@/assets/icons/bought.svg";
import { ReactComponent as DeleteIcon } from "@/assets/icons/delete.svg";
import { ReactComponent as NewEditionIcon } from "@/assets/icons/new-edition.svg";
import {
  successNotification,
  cleanNotifications,
} from "@/helpers/notifications";
import DetailsModal from "../DetailsModal/DetailsModal";
import { useCart } from "@/context/CartContext";
import { Issue, Publication } from "@/api/types";
import dayjs from "dayjs";
import "./ListView.css";
import clsx from "clsx";

interface ListViewProps {
  type: "issue" | "publication";
  items: Issue[] | Publication[];
  myItems: Issue[] | Publication[];
  openFlipbook: (pdf: string) => void;
}

const isIssue = (item: Issue | Publication): item is Issue => {
  return "month" in item && "year" in item;
};

const ListView: React.FC<ListViewProps> = ({
  type,
  openFlipbook,
  items,
  myItems,
}) => {
  const navigate = useNavigate();
  const typeIsIssue = type === "issue";
  const [opened, { open, close }] = useDisclosure(false);
  const [expandedDesc, setExpandedDesc] = useState<Record<number, boolean>>({});
  const [showToggle, setShowToggle] = useState<Record<number, boolean>>({});
  const isDesktop = window.innerWidth > 665;

  const isNew = (item: Issue | Publication) => {
    if (isIssue(item))
      return (
        item.month.toLowerCase() === dayjs().format("M") &&
        item.year === dayjs().format("YYYY")
      );
    else return dayjs(item.createdAt).format("M") === dayjs().format("M");
  };

  const [activeItem, setActiveItem] = useState<Issue | Publication>();
  const { cart, addToCart, removeFromCart } = useCart();

  const formatMonth = (month: string) => {
    let formatted = dayjs(month).locale("hr").format("MMMM");
    formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
    return formatted;
  };

  const openItemModale = (item: Issue) => {
    if (window.innerWidth > 665) return;
    setActiveItem(item);
    open();
  };

  const handleAddToCart = (item: Issue | Publication) => {
    addToCart({ ...item, cartId: `${type}-${item.id}`, type });
    successNotification({
      message: (
        <div className="add-to-cart-notification-group">
          <span>
            {typeIsIssue ? "Revija" : "Publikacija"} je dodana u košaricu!
          </span>
          <span
            className="add-to-cart-notification-link"
            onClick={() => {
              cleanNotifications();
              navigate("/cart");
            }}
          >
            Idi na košaricu
          </span>
        </div>
      ),
    });
  };

  const handleRemoveFromCart = (id: number) => {
    removeFromCart(`${type}-${id}`);
    successNotification({
      message: `${
        typeIsIssue ? "Revija" : "Publikacija"
      } je uklonjena iz košarice`,
    });
  };

  const descriptionRefs = useRef<Record<number, HTMLDivElement | null>>({});
  useEffect(() => {
    if (window.innerWidth <= 665) return;

    const toggles: Record<number, boolean> = {};

    Object.entries(descriptionRefs.current).forEach(([id, el]) => {
      if (el) {
        const isOverflowing = el.scrollHeight > el.clientHeight + 2;
        toggles[Number(id)] = isOverflowing;
      }
    });

    setShowToggle(toggles);
  }, [items]);

  return (
    <div className="list-view">
      {items?.length &&
        items.map((item) => {
          const isExpanded = expandedDesc[item.id];
          const shouldShowToggle = showToggle[item.id];

          return (
            <div
              key={item.id}
              className="list-item"
              onClick={() => openItemModale(item as Issue)}
            >
              <Image src={item.cover.url} className="list-image" />
              <div
                className={clsx(
                  "list-item-content",
                  isExpanded ? "expanded-item-content" : "fixed-item-content"
                )}
              >
                <div className="flex flex-col flex-1 h-full items-stretch content-stretch gap-4 justify-between">
                  <div className="flex flex-col gap-2 list-item-top">
                    <div className="flex flex-col gap-1">
                      {isNew(item) && (
                        <Group gap={10}>
                          <NewEditionIcon />
                          <div className="text-[#918865] text-sm font-bold">
                            Novo izdanje
                          </div>
                        </Group>
                      )}
                      <div className="text-[#0b1a29] text-base font-bold">
                        {item.title}
                      </div>
                    </div>
                    {isIssue(item) ? (
                      <div className="text-[#515151] text-xs font-medium">
                        {formatMonth(item.month)}, {item.year}
                      </div>
                    ) : (
                      <div className="text-[#515151] text-xs font-medium">
                        {item.author}
                      </div>
                    )}
                    <div
                      ref={(el) => {
                        descriptionRefs.current[item.id] = el;
                      }}
                      className={clsx(
                        "list-item-desc text-[#515151] text-sm font-medium",
                        isDesktop && !isExpanded && "clamped-desc"
                      )}
                    >
                      {item.description}
                    </div>
                    {isDesktop && shouldShowToggle && (
                      <div
                        className="read-more-toggle"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedDesc((prev) => ({
                            ...prev,
                            [item.id]: !prev[item.id],
                          }));
                        }}
                      >
                        {isExpanded ? "prikaži manje" : "pročitaj više"}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 list-item-bottom">
                    {myItems?.map((item) => item.id)?.includes(item.id) ? (
                      <Group gap={5}>
                        <BoughtIcon />
                        <div className="text-[#515151] text-sm font-medium">
                          Kupljeno
                        </div>
                      </Group>
                    ) : (
                      <div className="text-[#004a99] text-sm font-bold">
                        {item.price}€
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="list-buttons">
                {!myItems?.map((item) => item.id)?.includes(item.id) ? (
                  cart
                    .map((item) => item.cartId)
                    .includes(`${type}-${item.id}`) ? (
                    <Button
                      variant="filled"
                      color="#004a99"
                      radius="xl"
                      size="md"
                      className="list-button"
                      leftSection={<DeleteIcon fill="white" />}
                      onClick={() => handleRemoveFromCart(item.id)}
                    >
                      Ukloni iz košarice
                    </Button>
                  ) : (
                    <Button
                      variant="filled"
                      color="#004a99"
                      radius="xl"
                      size="md"
                      className="list-button"
                      onClick={() => handleAddToCart(item as Issue)}
                    >
                      Dodaj u košaricu
                    </Button>
                  )
                ) : (
                  <Button
                    variant="outline"
                    color="#004a99"
                    radius="xl"
                    size="md"
                    className="list-button"
                    onClick={() => openFlipbook(item.pdf.url)}
                  >
                    Pregledaj {typeIsIssue && "reviju"}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      {activeItem && (
        <DetailsModal
          opened={opened}
          onClose={close}
          type={type}
          openFlipbook={openFlipbook}
          item={activeItem}
          myItems={myItems}
        />
      )}
    </div>
  );
};

export default ListView;
